import React from 'react';

interface BenefitsVisualizationProps {
  benefitsText: string;
  className?: string;
}

interface Benefit {
  text: string;
}

const BenefitsVisualization: React.FC<BenefitsVisualizationProps> = ({ 
  benefitsText, 
  className = '' 
}) => {

  // Parse the benefits text to extract bullet points
  const parseBenefits = (text: string): Benefit[] => {
    const benefits: Benefit[] = [];
    
    // Split by newlines and match patterns like "- Text"
    const lines = text.split('\n');
    
    for (const line of lines) {
      const match = line.match(/^-\s*(.+)$/);
      if (match) {
        const benefitText = match[1].trim();
        
        if (benefitText) {
          benefits.push({ text: benefitText });
        }
      }
    }
    
    return benefits;
  };

  const benefits = parseBenefits(benefitsText);

  if (benefits.length === 0) {
    // Fallback to plain text if parsing fails
    return (
      <div className={`text-gray-700 leading-relaxed whitespace-pre-line ${className}`}>
        {benefitsText}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Desktop View - Grid Layout */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {benefits.map((benefit, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-lg p-4 border border-gray-200 transition-colors duration-200 hover:bg-blue-50 hover:border-blue-200 min-h-[80px] flex items-center"
          >
            <div className="flex items-start gap-3 w-full">
              {/* Checkmark icon */}
              <div className="flex-shrink-0 text-blue-500">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {benefit.text}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile View - Vertical List */}
      <div className="md:hidden space-y-3">
        {benefits.map((benefit, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-lg p-4 border border-gray-200 transition-colors duration-200 hover:bg-blue-50 hover:border-blue-200"
          >
            <div className="flex items-start gap-3">
              {/* Checkmark icon */}
              <div className="flex-shrink-0 text-blue-500">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {benefit.text}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BenefitsVisualization;
